import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  DEFAULT_OBSERVATION_DAYS,
  OPEN_TRACKING_NOTE,
  REFERENCE_THRESHOLD,
  formatCountDelta,
  formatNumber,
  formatRate,
  formatRateDelta,
  formatShortDate,
  messages,
  type AnalyticsSummary,
  type BreakdownRow,
  type FunnelStage,
  type Suggestion,
  type TimeseriesPoint,
  type TrackingStats,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { MetricCard, PeriodBanner, EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { cn, tabTriggerClass } from '@/lib/utils';

const TABS = [
  { value: 'summary', label: messages.analytics.tabSummary },
  { value: 'timeseries', label: messages.analytics.tabTimeseries },
  { value: 'breakdown', label: messages.analytics.tabBreakdown },
  { value: 'funnel', label: messages.analytics.tabFunnel },
  { value: 'suggestions', label: messages.analytics.tabSuggestions },
];

/** S60 分析 */
export const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const { filters, setFilter } = useUrlFilters({
    tab: 'summary',
    from: '',
    to: '',
    observationDays: String(DEFAULT_OBSERVATION_DAYS),
    campaignId: '',
    productId: '',
    assigneeId: '',
    dimension: 'campaign',
  });

  const query = {
    from: filters.from || undefined,
    to: filters.to || undefined,
    observationDays: filters.observationDays,
    campaignId: filters.campaignId || undefined,
    productId: filters.productId || undefined,
    assigneeId: filters.assigneeId || undefined,
  };

  const { data: summary, isLoading } = useQuery({
    queryKey: qk.analyticsSummary(query),
    queryFn: () => api.get<SingleResponse<AnalyticsSummary>>('/analytics/summary', query),
  });

  const tab = filters.tab as string;

  return (
    <>
      <PageHeader title={messages.analytics.title} />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <label className="text-note text-foreground-muted">
            {messages.analytics.sendPeriod}
            <div className="mt-1 flex items-center gap-1">
              <Input
                type="date"
                value={filters.from as string}
                onChange={(e) => setFilter('from', e.target.value)}
                aria-label="送信期間の開始日"
                className="w-40"
              />
              <span aria-hidden>–</span>
              <Input
                type="date"
                value={filters.to as string}
                onChange={(e) => setFilter('to', e.target.value)}
                aria-label="送信期間の終了日"
                className="w-40"
              />
            </div>
          </label>
          <label className="text-note text-foreground-muted">
            {messages.analytics.observationPeriod}
            <Select
              value={filters.observationDays as string}
              onChange={(e) => setFilter('observationDays', e.target.value)}
              aria-label="観測期間"
              className="mt-1 w-28"
            >
              {[7, 14, 30, 60].map((days) => (
                <option key={days} value={days}>
                  {days} 日
                </option>
              ))}
            </Select>
          </label>
        </CardContent>
      </Card>

      {summary && (
        <PeriodBanner
          from={summary.data.period.from}
          to={summary.data.period.to}
          observationDays={summary.data.period.observationDays}
          measuringCount={summary.data.period.measuringCount}
          className="mb-4"
        />
      )}

      {!hasRole('manager') && (
        <p className="mb-3 text-note text-foreground-muted">{messages.analytics.memberScopeNote}</p>
      )}

      <Tabs.Root value={tab} onValueChange={(v) => setFilter('tab', v)}>
        <Tabs.List className="scroll-x mb-4 flex gap-1 border-b border-border" aria-label="分析の表示">
          {TABS.map((item) => (
            <Tabs.Trigger
              key={item.value}
              value={item.value}
              className={tabTriggerClass}
            >
              {item.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="summary" className="space-y-4">
          {isLoading || !summary ? (
            <SkeletonRows rows={4} />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <MetricCard
                  label={messages.analytics.collected}
                  value={formatNumber(summary.data.collected)}
                  unit="件"
                  onClick={() => navigate('/companies')}
                />
                <MetricCard
                  label={`${messages.analytics.sent}済み`}
                  value={formatNumber(summary.data.sentCompanies)}
                  unit="社"
                  delta={formatCountDelta(summary.data.comparedToPrevious.sentCompanies)}
                  onClick={() => navigate('/companies?status=sent')}
                  isReference={summary.data.isReference}
                />
                <MetricCard
                  label={messages.analytics.replied}
                  value={formatNumber(summary.data.repliedCompanies)}
                  unit="社"
                  note={formatRate(summary.data.replyRate)}
                  delta={formatRateDelta(summary.data.comparedToPrevious.replyRate)}
                  onClick={() => navigate('/companies?status=replied')}
                  isReference={summary.data.isReference}
                />
                <MetricCard
                  label={messages.analytics.appointment}
                  value={formatNumber(summary.data.appointmentCompanies)}
                  unit="社"
                  note={formatRate(summary.data.appointmentRate)}
                  delta={formatRateDelta(summary.data.comparedToPrevious.appointmentRate)}
                  onClick={() => navigate('/companies?status=appointment')}
                  isReference={summary.data.isReference}
                />
              </div>

              {summary.data.isReference && (
                <p className="text-note text-foreground-muted">
                  {messages.analytics.referenceNote(REFERENCE_THRESHOLD)}
                </p>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>{messages.analytics.excludedTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="tabular grid grid-cols-2 gap-3 text-body sm:grid-cols-3 lg:grid-cols-5">
                    {[
                      { label: '失敗', value: summary.data.excluded.failed },
                      { label: '結果不明', value: summary.data.excluded.unknown },
                      { label: 'バウンス', value: summary.data.excluded.bounce },
                      { label: '要対応', value: summary.data.excluded.needsAction },
                      { label: 'スキップ', value: summary.data.excluded.skipped },
                    ].map((item) => (
                      <div key={item.label}>
                        <dt className="text-note text-foreground-muted">{item.label}</dt>
                        <dd className="text-h3">{formatNumber(item.value)}</dd>
                      </div>
                    ))}
                  </dl>
                  {summary.data.excluded.skippedByReason && (
                    <p className="mt-2 text-note text-foreground-muted">
                      スキップの内訳:{' '}
                      {Object.entries(summary.data.excluded.skippedByReason)
                        .map(([reason, count]) => `${reason} ${count}`)
                        .join(' / ')}
                    </p>
                  )}
                </CardContent>
              </Card>

              <MailTracking
                query={{ campaignId: query.campaignId, from: query.from, to: query.to }}
              />
            </>
          )}
        </Tabs.Content>

        <Tabs.Content value="timeseries">
          <TimeseriesTab query={query} />
        </Tabs.Content>

        <Tabs.Content value="breakdown">
          <BreakdownTab
            query={query}
            dimension={filters.dimension as string}
            onDimensionChange={(v) => setFilter('dimension', v)}
          />
        </Tabs.Content>

        <Tabs.Content value="funnel">
          <FunnelTab query={query} />
        </Tabs.Content>

        <Tabs.Content value="suggestions">
          <SuggestionsTab />
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
};

/** 推移。系列は明度と線種で区別し、凡例より直接ラベルを優先する */
const TimeseriesTab = ({ query }: { query: Record<string, unknown> }) => {
  const { data, isLoading } = useQuery({
    queryKey: qk.analyticsTimeseries(query),
    queryFn: () =>
      api.get<{ data: TimeseriesPoint[]; meta: Record<string, unknown> }>('/analytics/timeseries', {
        ...query,
        granularity: 'day',
      }),
  });

  if (isLoading) return <SkeletonRows rows={4} />;
  if (!data || data.data.length === 0) return <EmptyState title="対象の期間にデータがありません" />;

  const chartData = data.data.map((point) => ({ ...point, label: formatShortDate(point.date) }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>推移（日次）</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 56, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--foreground-muted)', fontSize: 12 }}
                stroke="var(--border-strong)"
              />
              <YAxis
                tick={{ fill: 'var(--foreground-muted)', fontSize: 12 }}
                stroke="var(--border-strong)"
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--surface-raised)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 8,
                  color: 'var(--foreground)',
                  fontSize: 12,
                }}
              />
              {/* 明度＋線種で系列を区別する（モノトーン） */}
              <Line
                type="monotone"
                dataKey="sentCompanies"
                name={messages.analytics.sent}
                stroke="var(--chart-1)"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="repliedCompanies"
                name={messages.analytics.replied}
                stroke="var(--chart-2)"
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="appointmentCompanies"
                name={messages.analytics.appointment}
                stroke="var(--chart-3)"
                strokeWidth={2}
                strokeDasharray="2 3"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <ul className="mt-3 flex flex-wrap gap-4 text-note">
          {[
            { label: messages.analytics.sent, style: 'border-t-2 border-solid', color: 'var(--chart-1)' },
            { label: messages.analytics.replied, style: 'border-t-2 border-dashed', color: 'var(--chart-2)' },
            { label: messages.analytics.appointment, style: 'border-t-2 border-dotted', color: 'var(--chart-3)' },
          ].map((item) => (
            <li key={item.label} className="flex items-center gap-1.5">
              <span
                aria-hidden
                className={cn('inline-block w-6', item.style)}
                style={{ borderColor: item.color }}
              />
              {item.label}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

/** 比較。件数が閾値未満の行は「参考値」と注記する */
const BreakdownTab = ({
  query,
  dimension,
  onDimensionChange,
}: {
  query: Record<string, unknown>;
  dimension: string;
  onDimensionChange: (value: string) => void;
}) => {
  const { data, isLoading } = useQuery({
    queryKey: qk.analyticsBreakdown({ ...query, dimension }),
    queryFn: () =>
      api.get<{ data: BreakdownRow[]; meta: Record<string, unknown> }>('/analytics/breakdown', {
        ...query,
        dimension,
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>比較</CardTitle>
        <Select
          value={dimension}
          onChange={(e) => onDimensionChange(e.target.value)}
          aria-label="比較の軸"
          className="w-40"
        >
          <option value="campaign">キャンペーン別</option>
          <option value="user">担当者別</option>
          <option value="product">商材別</option>
          <option value="channel">チャネル別</option>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : !data || data.data.length === 0 ? (
          <p className="text-body text-foreground-muted">対象の期間にデータがありません</p>
        ) : (
          <>
            {/* グラフ：対象ごとの 送信／返信／アポ 社数を横棒で比較 */}
            <div className="mb-4 w-full" style={{ height: Math.max(data.data.length * 48 + 24, 140) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.data.map((row) => ({
                    label: row.label,
                    送信: row.counts.sentCompanies,
                    返信: row.counts.repliedCompanies,
                    アポ: row.counts.appointmentCompanies,
                  }))}
                  layout="vertical"
                  margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                  barCategoryGap="24%"
                >
                  <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    tick={{ fill: 'var(--foreground-muted)', fontSize: 12 }}
                    stroke="var(--border-strong)"
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={120}
                    tick={{ fill: 'var(--foreground-muted)', fontSize: 12 }}
                    stroke="var(--border-strong)"
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--surface-raised)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 8,
                      color: 'var(--foreground)',
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="送信" fill="var(--chart-1)" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="返信" fill="var(--chart-2)" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="アポ" fill="var(--chart-3)" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* PC：表 */}
            <div className="hidden overflow-x-auto lg:block">
              <table className="w-full text-cell">
                <thead>
                  <tr className="border-b border-border-strong text-note text-foreground-muted">
                    <th scope="col" className="py-2 text-left">対象</th>
                    <th scope="col" className="py-2 text-right">送信</th>
                    <th scope="col" className="py-2 text-right">返信</th>
                    <th scope="col" className="py-2 text-right">返信率</th>
                    <th scope="col" className="py-2 text-right">アポ</th>
                    <th scope="col" className="py-2 text-right">アポ率</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((row) => (
                    <tr key={`${row.dimensionType}-${row.dimensionId}`} className="border-b border-border last:border-0">
                      <td className="py-2">
                        {row.label}
                        {row.isReference && (
                          <span className="ml-2 rounded-control border border-dashed border-border-strong px-1.5 text-note text-foreground-muted">
                            {messages.analytics.reference}
                          </span>
                        )}
                      </td>
                      <td className="tabular py-2 text-right">{formatNumber(row.counts.sentCompanies)}</td>
                      <td className="tabular py-2 text-right">{formatNumber(row.counts.repliedCompanies)}</td>
                      <td className="tabular py-2 text-right">{formatRate(row.rates.replyRate)}</td>
                      <td className="tabular py-2 text-right">{formatNumber(row.counts.appointmentCompanies)}</td>
                      <td className="tabular py-2 text-right">{formatRate(row.rates.appointmentRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* スマホ：明細カード */}
            <ul className="space-y-2 lg:hidden">
              {data.data.map((row) => (
                <li
                  key={`${row.dimensionType}-${row.dimensionId}`}
                  className="rounded-card border border-border p-3"
                >
                  <p className="flex items-center gap-2 text-body font-semibold">
                    {row.label}
                    {row.isReference && (
                      <span className="rounded-control border border-dashed border-border-strong px-1.5 text-note font-normal text-foreground-muted">
                        {messages.analytics.reference}
                      </span>
                    )}
                  </p>
                  <dl className="tabular mt-2 grid grid-cols-2 gap-2 text-note">
                    <div>
                      <dt className="text-foreground-muted">送信</dt>
                      <dd>{formatNumber(row.counts.sentCompanies)} 社</dd>
                    </div>
                    <div>
                      <dt className="text-foreground-muted">返信率</dt>
                      <dd>{formatRate(row.rates.replyRate)}</dd>
                    </div>
                    <div>
                      <dt className="text-foreground-muted">アポ</dt>
                      <dd>{formatNumber(row.counts.appointmentCompanies)} 社</dd>
                    </div>
                    <div>
                      <dt className="text-foreground-muted">アポ率</dt>
                      <dd>{formatRate(row.rates.appointmentRate)}</dd>
                    </div>
                  </dl>
                </li>
              ))}
            </ul>

            {(data.meta.uniqueNote as string | null) && (
              <p className="mt-3 text-note text-foreground-muted">{data.meta.uniqueNote as string}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const FunnelTab = ({ query }: { query: Record<string, unknown> }) => {
  const { data, isLoading } = useQuery({
    queryKey: qk.analyticsFunnel(query),
    queryFn: () =>
      api.get<{ data: FunnelStage[]; meta: Record<string, unknown> }>('/analytics/funnel', query),
  });

  if (isLoading) return <SkeletonRows rows={4} />;
  if (!data) return null;

  const max = Math.max(...data.data.map((s) => s.count), 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>ファネル</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {data.data.map((stage) => (
            <li key={stage.key} className="space-y-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-body font-semibold">{stage.label}</span>
                <span className="tabular text-body">
                  {formatNumber(stage.count)}
                  {stage.conversionFromPrevious != null && (
                    <span className="ml-2 text-note text-foreground-muted">
                      {formatRate(stage.conversionFromPrevious)}
                    </span>
                  )}
                  {stage.medianDaysFromPrevious != null && (
                    <span className="ml-2 text-note text-foreground-muted">
                      {messages.analytics.medianDays(stage.medianDaysFromPrevious)}
                    </span>
                  )}
                </span>
              </div>
              <div className="h-6 w-full overflow-hidden rounded-control bg-muted">
                <div
                  className="h-full bg-foreground"
                  style={{ width: `${Math.max((stage.count / max) * 100, 1)}%` }}
                />
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
};

/** AI 提案。根拠を展開表示し、採用／却下できる */
const SuggestionsTab = () => {
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: qk.suggestions({ decision: 'pending' }),
    queryFn: () => api.get<ListResponse<Suggestion>>('/suggestions', { decision: 'pending', limit: 30 }),
  });

  const refresh = useMutation({
    mutationFn: () => api.post('/suggestions/refresh'),
    onSuccess: () => {
      toast.success('提案の生成を開始しました', { description: '完了すると通知でお知らせします' });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'adopt' | 'reject' }) =>
      api.post(`/suggestions/${id}/${decision}`, {}),
    onSuccess: () => {
      toast.success('反映しました');
      void queryClient.invalidateQueries({ queryKey: ['suggestions'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="space-y-3">
      {hasRole('manager') && (
        <Button variant="outline" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw aria-hidden className={cn(refresh.isPending && 'animate-spin')} />
          {messages.suggestion.refresh}
        </Button>
      )}

      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title={messages.empty.suggestions.title}
          description={messages.empty.suggestions.description}
          icon={<Sparkles className="size-8" />}
        />
      ) : (
        <ul className="space-y-3">
          {data.data.map((suggestion) => (
            <li key={suggestion.id}>
              <Card>
                <CardContent className="space-y-2 pt-4 lg:pt-5">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="text-h3">{suggestion.title}</span>
                    {/* 推測は仮説として区別する */}
                    {suggestion.isHypothesis && (
                      <span className="rounded-control border border-dashed border-border-strong px-2 text-note text-foreground-muted">
                        {messages.suggestion.hypothesis}
                      </span>
                    )}
                  </p>
                  <p className="whitespace-pre-wrap text-body text-foreground-muted">{suggestion.body}</p>

                  <details className="rounded-control border border-border p-3">
                    <summary className="cursor-pointer text-body font-semibold">
                      {messages.suggestion.rationale}
                    </summary>
                    <dl className="mt-2 space-y-1 text-note text-foreground-muted">
                      {suggestion.rationale.dataPeriod && (
                        <div className="flex gap-2">
                          <dt>{messages.suggestion.dataPeriod}</dt>
                          <dd className="tabular">
                            {suggestion.rationale.dataPeriod.from}–{suggestion.rationale.dataPeriod.to}
                          </dd>
                        </div>
                      )}
                      {suggestion.rationale.sampleSize != null && (
                        <div className="flex gap-2">
                          <dt>対象件数</dt>
                          <dd className="tabular">{formatNumber(suggestion.rationale.sampleSize)}</dd>
                        </div>
                      )}
                      {suggestion.rationale.note && <p>{suggestion.rationale.note}</p>}
                    </dl>
                    {suggestion.isHypothesis && (
                      <p className="mt-2 text-note font-semibold">{messages.suggestion.hypothesisNote}</p>
                    )}
                  </details>

                  <div className="flex gap-2 border-t border-border pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => decide.mutate({ id: suggestion.id, decision: 'adopt' })}
                    >
                      {messages.suggestion.adopt}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => decide.mutate({ id: suggestion.id, decision: 'reject' })}
                    >
                      {messages.suggestion.reject}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** メールの開封・クリック（F07.7）。メールで送った分だけが対象になる */
const MailTracking = ({
  query,
}: {
  query: { campaignId?: string; from?: string; to?: string };
}) => {
  // 効果測定は「送信そのものの実績」。分析期間に縛ると期間外の送信が 0 件になりカードごと消えるため、
  // 期間フィルタは外して全期間で見る（キャンペーン指定があればその範囲）。
  const tquery = { campaignId: query.campaignId };
  const { data } = useQuery({
    queryKey: qk.trackingStats(tquery),
    queryFn: () => api.get<SingleResponse<TrackingStats>>('/tracking/stats', tquery),
  });

  const stats = data?.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{messages.tracking.title}</CardTitle>
      </CardHeader>
      <CardContent>
        {!stats || stats.trackedSends === 0 ? (
          <p className="text-note text-foreground-muted">計測対象のメール送信がまだありません。</p>
        ) : (
        <>
        <dl className="tabular grid grid-cols-2 gap-3 text-body sm:grid-cols-3 lg:grid-cols-5">
          {[
            { label: messages.tracking.trackedSends, value: `${formatNumber(stats.trackedCompanies)} 社` },
            {
              label: messages.tracking.openRate,
              value: formatRate(stats.openRate),
              note: `${formatNumber(stats.openedCompanies)} 社`,
            },
            {
              label: messages.tracking.clickRate,
              value: formatRate(stats.clickRate),
              note: `${formatNumber(stats.clickedCompanies)} 社`,
            },
            { label: messages.tracking.bounced, value: `${formatNumber(stats.bouncedCompanies)} 社` },
            {
              label: messages.tracking.unsubscribed,
              value: `${formatNumber(stats.unsubscribedCompanies)} 社`,
            },
          ].map((item) => (
            <div key={item.label}>
              <dt className="text-note text-foreground-muted">{item.label}</dt>
              <dd className="text-h3">{item.value}</dd>
              {item.note && <p className="text-note text-foreground-muted">{item.note}</p>}
            </div>
          ))}
        </dl>
        <div className="mt-3 space-y-2">
          {[
            { label: messages.tracking.openRate, n: stats.openedCompanies },
            { label: messages.tracking.clickRate, n: stats.clickedCompanies },
            { label: messages.tracking.unsubscribed, n: stats.unsubscribedCompanies },
          ].map((r) => {
            const pct =
              stats.trackedCompanies > 0
                ? Math.min(Math.round((r.n / stats.trackedCompanies) * 100), 100)
                : 0;
            return (
              <div key={r.label} className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-note text-foreground-muted">{r.label}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-control bg-muted">
                  <div className="h-full bg-foreground transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="tabular w-16 shrink-0 text-right text-note text-foreground-muted">
                  {formatNumber(r.n)} 社
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-note text-foreground-muted">{OPEN_TRACKING_NOTE}</p>
        {stats.topLinks.length > 0 && (
          <div className="mt-3 border-t border-border pt-3">
            <p className="mb-1.5 text-note font-semibold">{messages.tracking.topLinks}</p>
            <ul className="space-y-1">
              {[
                ...stats.topLinks.filter((l) => l.kind !== 'unsubscribe').slice(0, 5),
                ...stats.topLinks.filter((l) => l.kind === 'unsubscribe'),
              ].map((link) => (
                <li key={link.url} className="flex flex-wrap items-baseline gap-2">
                  <span className="min-w-0 flex-1 break-all text-note">
                    {link.kind === 'unsubscribe' ? messages.tracking.unsubscribeLink : link.url}
                  </span>
                  <span className="tabular text-note text-foreground-muted">
                    {formatNumber(link.companies)} 社
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
};
